import angular from 'angular'
import api, { AbstractCollectionService } from 'components/api'
import moment from 'moment'
import { colourForPlaylist, readableOn } from 'common/playlist-colour'
import { calendarEventId } from 'common/calendar-event-id'
import { DROP_SIDES } from '../../shared/drop-position'

class SceneCollectionService extends AbstractCollectionService {
  constructor ($q, $log, ApiService, StateService, toaster) {
    'ngInject'

    super($q, ApiService, StateService, 'SceneCollection', 'scenecollection')

    this._ToastService = toaster
    this.$q = $q
    this.$log = $log
    this.activeSceneCollectionId = null
    ApiService.subscribeToModel('scenecollection_scene', {}, this.sceneCollectionSceneSubscriber.bind(this))
  }

  sceneCollectionSceneSubscriber (error, change) {
    if (!error && change.sceneCollectionId && change.sceneId) {
      this.updatePropertyLocally(change.sceneCollectionId, 'updatedAt', change.updatedAt)
    }
  }

  getSceneCollectionsForScene (sceneId) {
    return this.ApiService.call('scenecollection/getSceneCollections', { sceneId })
  }

  getActiveSceneCollectionId (sceneId) {
    return this.ApiService.call('scene/getActiveSceneCollectionId', { sceneId })
  }

  addInterval (interval) {
    // The role decides the priority, so it has to travel. The number itself does
    // not: the server assigns it and no caller here sets one.
    const payload = {
      id: interval.id,
      sceneCollectionId: interval.sceneCollectionId,
      interval_start: interval.interval_start,
      interval_end: interval.interval_end,
      role: interval.role
    }

    // Only sent when the operator asked for it. Absent means leave the ordering
    // alone, which is not the same as asking for the lowest free value.
    if (interval.winsPeers) {
      payload.winsPeers = true
    }

    if (interval.baseUpdatedAt) {
      payload.baseUpdatedAt = interval.baseUpdatedAt
    }

    return this.ApiService.call('scenecollection/addInterval', payload)
  }

  /**
   * Edit an existing interval (wrapper around addInterval with ID)
   * @param {Object} interval - Interval data with id for update
   * @returns {Promise} Update response
   */
  editInterval (interval) {
    return this.addInterval(interval)
  }

  /**
   * Delete an interval by ID
   * @param {number} intervalId - ID of interval to delete
   * @returns {Promise} Delete response
   */
  deleteInterval (intervalId) {
    return this.ApiService.call('interval/deleteInterval', { id: intervalId })
  }

  /**
   * Check for intervals that never reach the screen.
   *
   * The full period is always checked: an interval loses to a neighbour the
   * calendar's current week need not even include.
   *
   * @param {number} [sceneId] - Limit the check to one scene. Left out, the
   *   server walks every scene.
   * @returns {Promise<Array>} [{ sceneId, sceneName, casualties }]
   */
  preflight (sceneId) {
    const payload = {}

    if (sceneId !== null && sceneId !== undefined) {
      payload.sceneId = sceneId
    }

    return this.ApiService.call('interval/preflight', payload)
  }

  playNow (sceneCollectionId) {
    return this.ApiService.call('scenecollection/playNow', { sceneCollectionId })
  }

  addSceneCollectionToScene (sceneId, sceneCollectionId) {
    return this.ApiService.call('scenecollection/addSceneCollectionToScene', { sceneId, sceneCollectionId })
  }

  /**
   * Reorder scene collections within a scene via drag-and-drop
   * @param {number} sceneId - The scene ID
   * @param {number} sourceCollectionId - ID of the dragged collection
   * @param {number} targetCollectionId - ID of the drop target collection
   * @param {string} side - 'before' or 'after'. The server places the playlist
   *   on that side of the target. It is required: the order of the stored list
   *   is not enough to say which side the operator saw.
   * @returns {Promise} Updated scene collection
   */
  reorderSceneCollections (sceneId, sourceCollectionId, targetCollectionId, side) {
    if (!DROP_SIDES.includes(side)) {
      return this.$q.reject(new Error(
        `reorderSceneCollections needs a side of ${DROP_SIDES.join(' or ')}`
      ))
    }

    return this.ApiService.call('scenecollection/reorderSceneCollections', {
      sceneId,
      sourceCollectionId,
      targetCollectionId,
      side
    })
  }

  addSceneCollectionItemToSceneCollection (mediaType, mediaId) {
    if (!this.activeSceneCollectionId) {
      this._ToastService.error('No playlist selected! Please select one by clicking the name on an item in scene content.')
      return this.$q.reject(new Error('No playlist selected! Please select one.'))
    }

    return this.ApiService.call('scenecollectionitem/addSceneCollectionItemToSceneCollection', {
      sceneCollectionId: this.activeSceneCollectionId,
      mediaType,
      mediaId
    })
  }

  setActiveSceneCollection (sceneCollectionId) {
    this.activeSceneCollectionId = sceneCollectionId
  }

  removeActiveSceneCollection () {
    this.activeSceneCollectionId = null
  }

  // ===== CALENDAR-SPECIFIC METHODS =====

  /**
   * Get calendar events for a specific scene within a date range
   * @param {number} sceneId - The scene ID
   * @param {string} startDate - Start date (ISO format)
   * @param {string} endDate - End date (ISO format)
   * @returns {Promise<Array>} Calendar events formatted for FullCalendar
   */
  getCalendarEvents (sceneId, startDate, endDate) {
    return this.ApiService.call('scenecollection/getCalendarEvents', {
      sceneId,
      startDate,
      endDate
    })
  }

  /**
   * Format intervals and scene collections for FullCalendar display
   * @param {Array} sceneCollections - Scene collections with intervals
   * @returns {Array} Formatted events for FullCalendar
   */
  formatIntervalsForCalendar (sceneCollections) {
    const events = []
    // getCalendarEvents returns every playlist of the scene, also the ones with
    // no interval in the window. The colours therefore stay the same in each week.
    const scenePlaylistIds = sceneCollections.map(sc => sc.id)

    sceneCollections.forEach(sc => {
      if (sc.intervals && sc.intervals.length > 0) {
        sc.intervals.forEach(interval => {
          // Handle zero-duration events by adding 1 hour default duration
          let endTime = interval.end_datetime
          if (interval.start_datetime === interval.end_datetime) {
            const startMoment = moment(interval.start_datetime)
            endTime = startMoment.add(1, 'hour').format('YYYY-MM-DD HH:mm:ss')
          }

          // Colour says which playlist it is. Whether the interval reaches the
          // screen is said by the on-screen strip and by a marker on the bar.
          const eventColor = colourForPlaylist(sc.id, scenePlaylistIds)

          events.push({
            id: calendarEventId(interval.id),
            title: sc.name,
            start: interval.start_datetime,
            end: endTime,
            backgroundColor: eventColor,
            borderColor: eventColor,
            textColor: readableOn(eventColor),
            extendedProps: {
              intervalId: interval.id,
              sceneCollectionId: sc.id,
              // The stored value, null included. A null loses every comparison in
              // the player, so the interval never reaches the screen, and saying
              // so is better than inventing a number for it.
              priority: interval.priority,
              // Derived by the server with roleFromPriority. The calendar panel
              // compares these roles, so the panel and the server name one band.
              role: interval.role || null,
              updatedAt: interval.updatedAt
            }
          })
        })
      }
    })

    return events
  }

  /**
   * Subscribe to calendar-related updates for a specific scene
   * @param {number} sceneId - The scene ID to monitor
   * @param {Function} callback - Callback for interval changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToCalendarUpdates (sceneId, callback) {
    // subscribeToModel resolves the subscription request. It does not return an
    // unsubscribe function. To detach a listener, pass the same listener back.
    const intervalListener = (error, change) => {
      if (!error) {
        callback(error, { type: 'interval', data: change })
      }
    }

    const sceneCollectionListener = (error, change) => {
      if (!error) {
        callback(error, { type: 'sceneCollection', data: change })
      }
    }

    // A playlist joins or leaves a scene. The calendar needs this event to keep
    // its collection-id set correct, and can then ignore the other scenes.
    const sceneMembershipListener = (error, change) => {
      if (!error) {
        callback(error, { type: 'sceneToCollection', data: change })
      }
    }

    this.ApiService.subscribeToModel('interval', {}, intervalListener)
    this.ApiService.subscribeToModel('scenecollection', {}, sceneCollectionListener)
    this.ApiService.subscribeToModel('scenecollection_scene', {}, sceneMembershipListener)

    const offReconnected = this.ApiService.onReconnected(() => callback(null, { type: 'reconnected' }))

    return () => {
      offReconnected()
      this.ApiService.unsubscribeFromModel('interval', intervalListener)
      this.ApiService.unsubscribeFromModel('scenecollection', sceneCollectionListener)
      this.ApiService.unsubscribeFromModel('scenecollection_scene', sceneMembershipListener)
    }
  }
}

export default angular.module('scenecollection.service', [
  api
])
  .service('SceneCollectionService', SceneCollectionService).name

export { SceneCollectionService }
